/**
 * Admin Courses Management Tab Component
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { coursesAPI } from '@/lib/api';
import { Plus, Search, Loader2, Trash2, BookOpen, Upload } from 'lucide-react';

export const CoursesTab = ({ courses, universities, onRefresh, onShowCSVImport }) => {
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [deletingCourseId, setDeletingCourseId] = useState(null);
  const [newCourse, setNewCourse] = useState({
    university_id: '',
    name: '',
    degree_level: 'bachelors',
    field_of_study: '',
    duration_months: 12,
    tuition_fee: '',
    intake_dates: '',
    ielts_requirement: 6.0,
    course_url: ''
  });

  const handleAddCourse = async (e) => {
    e.preventDefault();
    try {
      await coursesAPI.create({
        university_id: newCourse.university_id,
        name: newCourse.name,
        degree_level: newCourse.degree_level,
        field_of_study: newCourse.field_of_study,
        duration_months: parseInt(newCourse.duration_months),
        tuition_fee: parseFloat(newCourse.tuition_fee),
        intake_dates: newCourse.intake_dates,
        ielts_requirement: parseFloat(newCourse.ielts_requirement),
        course_url: newCourse.course_url || null
      });
      toast.success('Course added successfully');
      setShowAddCourse(false);
      setNewCourse({
        university_id: '',
        name: '',
        degree_level: 'bachelors',
        field_of_study: '',
        duration_months: 12,
        tuition_fee: '',
        intake_dates: '',
        ielts_requirement: 6.0,
        course_url: ''
      });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add course');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    setDeletingCourseId(courseId);
    try {
      await coursesAPI.delete(courseId);
      toast.success('Course deleted successfully');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete course');
    } finally {
      setDeletingCourseId(null);
    }
  };

  const filteredCourses = courses.filter(course => 
    course.name?.toLowerCase().includes(courseSearchTerm.toLowerCase()) ||
    course.university_name?.toLowerCase().includes(courseSearchTerm.toLowerCase()) ||
    course.field_of_study?.toLowerCase().includes(courseSearchTerm.toLowerCase())
  ).slice(0, 50);

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Manage Courses</h3>
            <p className="text-sm text-gray-500">{courses.length} total courses</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {onShowCSVImport && (
              <Button
                onClick={onShowCSVImport}
                variant="outline"
                className="border-teal-300 text-teal-700 hover:bg-teal-50 font-semibold"
                data-testid="courses-import-csv-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
            )}
            <Dialog open={showAddCourse} onOpenChange={setShowAddCourse}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 text-white hover:bg-teal-700 font-semibold" data-testid="add-course-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Course
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-xl">
              <DialogHeader>
                <DialogTitle className="text-gray-800">Add New Course</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddCourse} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="space-y-2">
                  <Label className="text-gray-600">University *</Label>
                  <Select 
                    value={newCourse.university_id} 
                    onValueChange={(val) => setNewCourse({ ...newCourse, university_id: val })}
                  >
                    <SelectTrigger className="bg-[#e9ecef] border-gray-200" data-testid="course-university">
                      <SelectValue placeholder="Select University" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#e9ecef] border-gray-200 max-h-60">
                      {universities.map(uni => (
                        <SelectItem key={uni.id} value={uni.id}>{uni.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-600">Course Name *</Label>
                  <Input
                    value={newCourse.name}
                    onChange={(e) => setNewCourse({ ...newCourse, name: e.target.value })}
                    className="bg-[#e9ecef] border-gray-200"
                    placeholder="e.g., MSc Computer Science"
                    required
                    data-testid="course-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-600">Degree Level *</Label>
                    <Select 
                      value={newCourse.degree_level} 
                      onValueChange={(val) => setNewCourse({ ...newCourse, degree_level: val })}
                    >
                      <SelectTrigger className="bg-[#e9ecef] border-gray-200" data-testid="course-level">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#e9ecef] border-gray-200">
                        <SelectItem value="bachelors">Bachelors</SelectItem>
                        <SelectItem value="masters">Masters</SelectItem>
                        <SelectItem value="phd">PhD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-600">Field of Study *</Label>
                    <Input
                      value={newCourse.field_of_study}
                      onChange={(e) => setNewCourse({ ...newCourse, field_of_study: e.target.value })}
                      className="bg-[#e9ecef] border-gray-200"
                      placeholder="e.g., Computer Science"
                      required
                      data-testid="course-field"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-600">Duration (months) *</Label>
                    <Input
                      type="number"
                      value={newCourse.duration_months}
                      onChange={(e) => setNewCourse({ ...newCourse, duration_months: e.target.value })}
                      className="bg-[#e9ecef] border-gray-200"
                      required
                      data-testid="course-duration"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-600">Tuition Fee (£) *</Label>
                    <Input
                      type="number"
                      value={newCourse.tuition_fee}
                      onChange={(e) => setNewCourse({ ...newCourse, tuition_fee: e.target.value })}
                      className="bg-[#e9ecef] border-gray-200"
                      placeholder="e.g., 15000"
                      required
                      data-testid="course-fee"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-600">IELTS Requirement</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={newCourse.ielts_requirement}
                      onChange={(e) => setNewCourse({ ...newCourse, ielts_requirement: e.target.value })}
                      className="bg-[#e9ecef] border-gray-200"
                      data-testid="course-ielts"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-600">Intake Dates *</Label>
                    <Input
                      value={newCourse.intake_dates}
                      onChange={(e) => setNewCourse({ ...newCourse, intake_dates: e.target.value })}
                      className="bg-[#e9ecef] border-gray-200"
                      placeholder="Sep 2025, Jan 2026"
                      required
                      data-testid="course-intakes"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-600">Course URL (optional)</Label>
                  <Input
                    value={newCourse.course_url}
                    onChange={(e) => setNewCourse({ ...newCourse, course_url: e.target.value })}
                    className="bg-[#e9ecef] border-gray-200"
                    placeholder="https://university.edu/course"
                    data-testid="course-url"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-teal-600 text-white hover:bg-teal-700 font-semibold" 
                  disabled={!newCourse.university_id}
                  data-testid="course-submit"
                >
                  Add Course
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={courseSearchTerm}
            onChange={(e) => setCourseSearchTerm(e.target.value)}
            className="bg-[#e9ecef] border-gray-200 pl-10"
            placeholder="Search courses by name, university, or field..."
            data-testid="course-search"
          />
        </div>

        {/* Courses List */}
        {filteredCourses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Course</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">University</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Level</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Fee</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCourses.map((course) => (
                  <tr key={course.id} className="border-b border-white/5 hover:bg-[#e9ecef]/30" data-testid={`course-row-${course.id}`}>
                    <td className="py-3 px-4">
                      <p className="text-gray-800 font-medium">{course.name}</p>
                      <p className="text-sm text-gray-500">{course.field_of_study}</p>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{course.university_name}</td>
                    <td className="py-3 px-4">
                      <Badge className="bg-blue-100 text-blue-600 capitalize">
                        {course.degree_level}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-teal-600 font-medium">
                      £{course.tuition_fee?.toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteCourse(course.id)}
                        disabled={deletingCourseId === course.id}
                        className="text-red-600 hover:bg-red-500/10"
                        data-testid={`delete-course-${course.id}`}
                      >
                        {deletingCourseId === course.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{courseSearchTerm ? 'No courses match your search' : 'No courses found'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoursesTab;
